import {
  boolean,
  index,
  integer,
  pgTable,
  primaryKey,
  text,
  timestamp,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";

/**
 * Instructors - ข้อมูลครูสอน
 * อัตราค่าจ้างต่อชั่วโมงอยู่ที่ instructor_payout_rates
 */
export const instructors = pgTable(
  "instructors",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** ชื่อครูสอน */
    name: text("name").notNull(),
    /** เบอร์โทรศัพท์ */
    phoneNumber: text("phone_number"),
    /** อีเมล */
    email: text("email"),
    /** เลขบัญชีธนาคารสำหรับโอนค่าจ้าง */
    bankAccountNumber: text("bank_account_number"),
    /** ประเภทการจ้างงาน: full_time (ประจำ) หรือ freelance (ฟรีแลนซ์) */
    employmentType: text("employment_type", {
      enum: ["full_time", "freelance"],
    }).notNull(),
    /**
     * Lead general_class sessions ฟรีต่อเดือนปฏิทิน (retainer quota)
     * ก่อนคิดค่าจ้างเพิ่ม — guest ใช้ 0 เสมอ
     */
    monthlyFreeGeneralClassSessions: integer(
      "monthly_free_general_class_sessions",
    )
      .notNull()
      .default(0),
    /**
     * Private Package (private_shared) ที่สตูดิโอ
     * package_tier = เรทตายตัวตามจำนวนคน
     * instructor_rate = เรทส่วนตัวของครูคนนี้
     */
    privatePackagePayoutMode: text("private_package_payout_mode", {
      enum: ["package_tier", "instructor_rate"],
    })
      .notNull()
      .default("package_tier"),
    /** สถานะ: active (ใช้งาน) หรือ inactive (ไม่ใช้งาน) */
    status: text("status", {
      enum: ["active", "inactive"],
    })
      .notNull()
      .default("active"),
    /**
     * เป็นครูชั่วคราว/guest หรือไม่
     * true = ครูชั่วคราว (สร้างจากหน้า check-in), false/null = ครูปกติ
     */
    isGuest: boolean("is_guest"),
    /**
     * วันที่สร้างครูชั่วคราว (optional)
     * ใช้สำหรับติดตามว่าครูชั่วคราวถูกสร้างเมื่อไหร่
     */
    guestCreatedAt: timestamp("guest_created_at", {
      mode: "string",
      withTimezone: false,
    }),
    /** หมายเหตุเพิ่มเติม */
    notes: text("notes"),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("instructors_by_status").on(t.status),
    index("instructors_by_employment_type").on(t.employmentType),
    index("instructors_by_created_by").on(t.createdBy),
    index("instructors_by_is_guest").on(t.isGuest),
  ],
);

/**
 * อัตราค่าจ้างผู้สอนต่อชั่วโมງ แยกตามประเภทงาน (คลาสกลุ่ม / private / partner)
 */
export const instructorPayoutRates = pgTable(
    "instructor_payout_rates",
    {
        instructorId: text("instructor_id")
            .notNull()
            .references(() => instructors.id, { onDelete: "cascade" }),
        workKind: text("work_kind", {
            enum: [
                "general_class",
                "private_lesson",
                "partner",
                "room_rental_assist",
                "general_class_assist",
            ],
        }).notNull(),
        locationKind: text("location_kind", {
            enum: ["studio", "off_site", "off_site_near", "off_site_far"],
        })
            .notNull()
            .default("studio"),
        payoutBasis: text("payout_basis", {
            enum: ["per_hour", "per_session"],
        })
            .notNull()
            .default("per_hour"),
        amountKip: integer("amount_kip").notNull(),
    },
    (t) => [
        primaryKey({
            columns: [t.instructorId, t.workKind, t.locationKind],
        }),
        index("instructor_payout_rates_by_instructor_id").on(t.instructorId),
    ],
);

/**
 * Rooms - ข้อมูลห้องเรียน
 * เก็บข้อมูลห้องเรียนที่ใช้สำหรับจัดการคลาสและเวิร์กช็อป
 */
export const rooms = pgTable(
  "rooms",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** ชื่อห้องเรียน */
    name: text("name").notNull(),
    /** ความจุสูงสุดของห้อง (จำนวนคน) */
    capacity: integer("capacity"),
    /** คำอธิบายเพิ่มเติมเกี่ยวกับห้อง */
    description: text("description"),
    /** สถานะ: active (ใช้งาน), inactive (ไม่ใช้งาน), maintenance (ซ่อมบำรุง) */
    status: text("status", {
      enum: ["active", "inactive", "maintenance"],
    })
      .notNull()
      .default("active"),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("rooms_by_status").on(t.status),
    index("rooms_by_created_by").on(t.createdBy),
  ],
);

/**
 * Class Types - ประเภทคลาส (Style)
 * เก็บข้อมูลประเภทหรือสไตล์ของการเต้น เช่น Hip-hop, Jazz, Contemporary
 */
export const classTypes = pgTable(
  "class_types",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** ชื่อประเภทคลาส (unique) */
    name: text("name").notNull().unique(),
    /** คำอธิบายเพิ่มเติม */
    description: text("description"),
    /** สถานะ: active (ใช้งาน) หรือ inactive (ไม่ใช้งาน) */
    status: text("status", {
      enum: ["active", "inactive"],
    })
      .notNull()
      .default("active"),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("class_types_by_status").on(t.status),
    index("class_types_by_created_by").on(t.createdBy),
  ],
);

/**
 * Levels - ระดับความยาก
 * เก็บข้อมูลระดับความยากของคลาส เช่น Beginner, Intermediate, Advanced
 */
export const levels = pgTable(
  "levels",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** ชื่อระดับ (unique) */
    name: text("name").notNull().unique(),
    /** คำอธิบายเพิ่มเติม */
    description: text("description"),
    /** ลำดับสำหรับ sorting (ใช้สำหรับเรียงลำดับจากง่ายไปยาก) */
    order: integer("order"),
    /** สถานะ: active (ใช้งาน) หรือ inactive (ไม่ใช้งาน) */
    status: text("status", {
      enum: ["active", "inactive"],
    })
      .notNull()
      .default("active"),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("levels_by_status").on(t.status),
    index("levels_by_order").on(t.order),
    index("levels_by_created_by").on(t.createdBy),
  ],
);

/**
 * Student Groups - กลุ่มนักเรียน
 * เก็บข้อมูลกลุ่มนักเรียนสำหรับแยกกลุ่มและจัด prefix ของ memberId
 */
export const studentGroups = pgTable(
  "student_groups",
  {
    /** Primary key - ใช้ nanoid() สำหรับ generate ID */
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** ชื่อกลุ่ม (unique) */
    name: text("name").notNull().unique(),
    /** Code/Prefix สำหรับใช้ใน memberId (unique) - เช่น G1, KIDS */
    code: text("code").notNull().unique(),
    /** คำอธิบายเพิ่มเติม */
    description: text("description"),
    /** ลำดับสำหรับ sorting */
    order: integer("order"),
    /** สถานะ: active (ใช้งาน) หรือ inactive (ไม่ใช้งาน) */
    status: text("status", {
      enum: ["active", "inactive"],
    })
      .notNull()
      .default("active"),
    /** ผู้สร้างข้อมูล - reference ไปยัง user table */
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    /** วันที่สร้างข้อมูล */
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    /** วันที่อัปเดตล่าสุด */
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("student_groups_by_status").on(t.status),
    index("student_groups_by_order").on(t.order),
    index("student_groups_by_created_by").on(t.createdBy),
    index("student_groups_by_code").on(t.code),
  ],
);
